import React from "react";

type LoaderSize = "small" | "default" | "large" | "chat" | number;

type PitaliyColorLoaderProps = {
  className?: string;
  label?: string;
  size?: LoaderSize;
  primaryColor?: string;
  secondaryColor?: string;
};

const scaleMap: Record<Exclude<LoaderSize, number>, number> = {
  chat: 0.24,
  small: 0.38,
  default: 0.56,
  large: 0.82,
};

const getScale = (size: LoaderSize) => {
  if (typeof size === "number") {
    return Math.max(0.2, size / 100);
  }

  return scaleMap[size];
};

const loaderStyles = `
.pitaliy-color-loader-container {
  --pitaliy-loader-scale: 0.56;
  --pitaliy-loader-primary: #7B43FF;
  --pitaliy-loader-secondary: #FF47C9;
  position: relative;
  width: calc(170px * var(--pitaliy-loader-scale));
  height: calc(130px * var(--pitaliy-loader-scale));
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: visible;
}

.pitaliy-color-loader-stage {
  position: relative;
  width: 170px;
  height: 130px;
  transform: scale(var(--pitaliy-loader-scale));
  transform-origin: center;
}

.pitaliy-color-loader-mark {
  position: absolute;
  width: 50px;
  height: 50px;
  top: 50%;
  left: 50%;
  margin-top: -25px;
  margin-left: -25px;
  background-color: var(--pitaliy-loader-primary);
  border-radius: 25px;
  transform: translateX(0px) translateY(0px) rotate(0deg);
  animation: pitaliyColorLoaderMain 2.6s ease 0s infinite forwards;
  z-index: 2;
}

.pitaliy-color-loader-mark::after {
  content: '';
  display: inline-block;
  position: absolute;
  width: 50px;
  height: 50px;
  top: 0;
  left: -50px;
  background-color: var(--pitaliy-loader-secondary);
  border-radius: 25px;
  transform-origin: top right;
  transform: translateX(0px) translateY(0px) rotate(0deg);
  animation: pitaliyColorLoaderSecondary 2.6s ease 0s infinite forwards;
}

.pitaliy-color-loader-shadow {
  position: absolute;
  width: 100px;
  height: 10px;
  top: 50%;
  left: 50%;
  margin-top: 50px;
  margin-left: -75px;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.15);
  transform: translateX(0px) translateY(0px) rotate(0deg);
  animation:
    pitaliyColorLoaderShadow 2.6s ease 0s infinite forwards,
    pitaliyColorLoaderFloat 5s ease-in-out 0s infinite forwards;
  filter: blur(5px);
  z-index: 1;
}

@keyframes pitaliyColorLoaderMain {
  0% {
    width: 50px;
    transform: translateX(0px) translateY(0px) rotate(0deg);
  }
  20% {
    width: 50px;
    transform: translateX(0px) translateY(0px) rotate(0deg);
  }
  40% {
    width: 150px;
    transform: translateX(-50px) translateY(0px) rotate(0deg);
  }
  60% {
    width: 150px;
    transform-origin: bottom right;
    transform: translateX(-150px) translateY(0px) rotate(90deg);
  }
  80% {
    width: 50px;
    transform: translateX(-25px) translateY(0px) rotate(90deg);
  }
  100% {
    width: 50px;
    transform: translateX(-50px) translateY(0px) rotate(90deg);
  }
}

@keyframes pitaliyColorLoaderSecondary {
  0% {
    transform: translateX(0px) translateY(0px) rotate(0deg);
  }
  20% {
    transform: translateX(0px) translateY(0px) rotate(180deg);
  }
  40% {
    transform: translateX(0px) translateY(0px) rotate(180deg);
  }
  60% {
    transform: translateX(0px) translateY(0px) rotate(0deg);
  }
  80% {
    transform: translateX(0px) translateY(0px) rotate(0deg);
  }
  100% {
    transform: translateX(0px) translateY(0px) rotate(180deg);
  }
}

@keyframes pitaliyColorLoaderShadow {
  0% {
    width: 150px;
    transform: translateX(-25px) translateY(0px) rotate(0deg);
  }
  20% {
    width: 75px;
    transform: translateX(37.5px) translateY(0px) rotate(0deg);
  }
  40% {
    width: 200px;
    transform: translateX(-25px) translateY(0px) rotate(0deg);
  }
  60% {
    width: 75px;
    transform: translateX(37.5px) translateY(0px) rotate(0deg);
  }
  80% {
    width: 75px;
    transform: translateX(37.5px) translateY(0px) rotate(0deg);
  }
  100% {
    width: 150px;
    transform: translateX(-25px) translateY(0px) rotate(0deg);
  }
}

@keyframes pitaliyColorLoaderFloat {
  0% { top: 50%; }
  50% { top: 51%; }
  100% { top: 50%; }
}
`;

export default function PitaliyColorLoader({
  className = "",
  label = "読み込み中",
  size = "default",
  primaryColor = "#7B43FF",
  secondaryColor = "#FF47C9",
}: PitaliyColorLoaderProps) {
  return (
    <div
      aria-label={label}
      className={`pitaliy-color-loader-container ${className}`}
      role="status"
      style={
        {
          "--pitaliy-loader-scale": getScale(size),
          "--pitaliy-loader-primary": primaryColor,
          "--pitaliy-loader-secondary": secondaryColor,
        } as React.CSSProperties
      }
    >
      <style>{loaderStyles}</style>
      <div className="pitaliy-color-loader-stage" aria-hidden="true">
        <div className="pitaliy-color-loader-mark" />
        <div className="pitaliy-color-loader-shadow" />
      </div>
    </div>
  );
}
